/*
 * Sample surveys (Customer Success → Surveys): the forms Demulla sends its
 * clients — ticket CSAT, quarterly NPS, onboarding / go-live, exit and demo
 * feedback — with their questions, triggers, channels, single-use links,
 * responses and the automations a low score sets off.
 *
 * Responses point at customers, contacts and tickets by id, and the CSAT and
 * NPS answers are the same ones Customer Success → Feedback shows
 * (mock/success.js) and the ratings clients post through the Support API
 * (POST /api/v1/support/tickets/{ref}/feedback, mock/support.js).
 */
import { reactive } from "vue";
import { daysFromNow } from "@/helpers/crm";
import { company, staffName, productById } from "@/mock/masters";
import { customers, customerById } from "@/mock/customers";
import { contacts } from "@/mock/contacts";
import { tickets, ticketFeedback, connectedApps, hoursAgo } from "@/mock/support";
import { csatResponses, npsResponses, npsTrend } from "@/mock/success";
import { configLog } from "@/mock/settings";

export const SURVEY_BASE_URL = "https://crm.demulla.co.ke/s/";
export const surveyLink = (token) => `${SURVEY_BASE_URL}${token}`;

/* ---------- Catalogues ---------- */

export const surveyTypes = [
  { key: "csat", name: "CSAT", tone: "primary", icon: "iconoir-star" },
  { key: "nps", name: "NPS", tone: "success", icon: "iconoir-stats-up-square" },
  { key: "onboarding", name: "Onboarding", tone: "info", icon: "iconoir-clipboard-check" },
  { key: "exit", name: "Exit", tone: "danger", icon: "iconoir-archive" },
  { key: "demo", name: "Demo feedback", tone: "purple", icon: "iconoir-eye" },
  { key: "custom", name: "Custom", tone: "secondary", icon: "iconoir-page" },
];
export const surveyType = (key) => surveyTypes.find((t) => t.key === key) ?? surveyTypes[5];

export const surveyStatuses = [
  { key: "draft", name: "Draft", tone: "secondary" },
  { key: "active", name: "Active", tone: "success" },
  { key: "paused", name: "Paused", tone: "warning" },
  { key: "closed", name: "Closed", tone: "dark" },
];
export const surveyStatus = (key) => surveyStatuses.find((s) => s.key === key) ?? surveyStatuses[0];

export const questionTypes = [
  { key: "rating", name: "Star rating 1–5 (CSAT)", icon: "iconoir-star", scored: true },
  { key: "nps", name: "NPS 0–10", icon: "iconoir-stats-up-square", scored: true },
  { key: "single", name: "Single choice", icon: "iconoir-list", options: true },
  { key: "multi", name: "Multiple choice", icon: "iconoir-task-list", options: true },
  { key: "short", name: "Short text", icon: "iconoir-text-box" },
  { key: "long", name: "Long text", icon: "iconoir-page" },
  { key: "yesno", name: "Yes / No", icon: "iconoir-check-circle" },
  { key: "date", name: "Date", icon: "iconoir-calendar" },
  { key: "matrix", name: "Matrix (rate several items)", icon: "iconoir-table" },
];
export const questionType = (key) => questionTypes.find((t) => t.key === key) ?? questionTypes[4];

export const logicOps = [
  { key: "lte", name: "≤" },
  { key: "gte", name: "≥" },
  { key: "eq", name: "is" },
  { key: "neq", name: "is not" },
  { key: "answered", name: "is answered" },
];
export const logicOp = (key) => logicOps.find((o) => o.key === key)?.name ?? key;

export const channelCatalogue = [
  { key: "email", name: "Email", icon: "iconoir-mail" },
  { key: "sms", name: "SMS", icon: "iconoir-message-text" },
  { key: "whatsapp", name: "WhatsApp", icon: "iconoir-chat-bubble" },
  { key: "in_product", name: "In-product (Support API)", icon: "iconoir-network" },
  { key: "link", name: "Public link / QR", icon: "iconoir-qr-code" },
];
export const channelName = (key) => channelCatalogue.find((c) => c.key === key)?.name ?? key;
export const channelIcon = (key) => channelCatalogue.find((c) => c.key === key)?.icon ?? "iconoir-send";

export const placeholders = ["{first_name}", "{customer}", "{product}", "{ticket}", "{ticket_subject}", "{company}", "{link}", "{expiry}"];

// Audiences a manual send or a scheduled trigger can pick.
export const audienceLists = [
  { key: "dm_live", name: "Decision makers · live customers", roles: ["decision_maker"], lifecycle: ["live"] },
  { key: "primary_live", name: "Primary contacts · live customers", roles: ["primary"], lifecycle: ["live"] },
  { key: "all_live", name: "All active contacts · live customers", roles: null, lifecycle: ["live"] },
  { key: "at_risk", name: "Primary + decision makers · at-risk customers", roles: ["primary", "decision_maker"], lifecycle: ["at_risk"] },
  { key: "onboarding", name: "Primary contacts · onboarding customers", roles: ["primary"], lifecycle: ["onboarding"] },
  { key: "lms", name: "Decision makers · Demulla LMS clients", roles: ["decision_maker", "primary"], lifecycle: ["live", "onboarding"], product: 2 },
];
export const audienceSize = (key, segments = []) => {
  const a = audienceLists.find((x) => x.key === key);
  if (!a) return 0;
  return contacts.filter((k) => {
    const c = k.customer_id && customerById(k.customer_id);
    if (!c || k.status !== "active" || !a.lifecycle.includes(c.lifecycle)) return false;
    if (a.product && !c.product_ids.includes(a.product)) return false;
    if (segments.length && !segments.includes(c.segment)) return false;
    if (!a.roles) return true;
    return a.roles.includes(k.role) || (a.roles.includes("decision_maker") && k.decision_maker);
  }).length;
};

/* ---------- Logic ---------- */

/** True when a condition { q, op, value } holds for the answers so far. */
export const conditionMet = (cond, answers) => {
  if (!cond || !cond.q) return true;
  const a = answers[cond.q];
  const empty = a === undefined || a === null || a === "" || (Array.isArray(a) && !a.length);
  if (cond.op === "answered") return !empty;
  if (empty) return false;
  const eq = Array.isArray(a) ? a.includes(cond.value) : String(a) === String(cond.value);
  switch (cond.op) {
    case "lte": return Number(a) <= Number(cond.value);
    case "gte": return Number(a) >= Number(cond.value);
    case "eq": return eq;
    case "neq": return !eq;
    default: return true;
  }
};
export const questionVisible = (q, answers) => !q.show_if || conditionMet(q.show_if, answers);

export const allQuestions = (survey) => survey.sections.flatMap((s) => s.questions);
export const questionLabel = (survey, id) => {
  const i = allQuestions(survey).findIndex((q) => q.id === id);
  return i < 0 ? "?" : `Q${i + 1}`;
};
// The question whose answer is the survey's score (first rating or NPS question).
export const scoreQuestion = (survey) => allQuestions(survey).find((q) => q.type === "rating" || q.type === "nps") ?? null;

/** {ticket}, {customer}… filled from a recipient's context. */
export const fillPlaceholders = (text, ctx = {}) =>
  String(text ?? "").replace(/\{(\w+)\}/g, (m, k) => (ctx[k] !== undefined && ctx[k] !== null ? ctx[k] : m));

/* ---------- Templates (questions) ---------- */

let qid = 1000;
let sid = 100;
export const nextQuestionId = () => ++qid;
export const nextSectionId = () => ++sid;

const Q = (type, text, extra = {}) => ({
  id: ++qid,
  type,
  text,
  help: "",
  required: false,
  options: [],
  rows: [],
  columns: [],
  show_if: null,
  ...extra,
});
const S = (title, description, questions, extra = {}) => ({ id: ++sid, title, description, questions, jump: null, ...extra });
export const blankQuestion = (type = "short") =>
  Q(type, "", {
    options: ["single", "multi"].includes(type) ? ["Option 1", "Option 2"] : [],
    rows: type === "matrix" ? ["Item 1", "Item 2"] : [],
    columns: type === "matrix" ? ["Poor", "Fair", "Good", "Excellent"] : [],
  });
export const blankSection = (n = 1) => S(`Section ${n}`, "", [blankQuestion("short")]);

const build = {
  csat: () => {
    const q1 = Q("rating", "How satisfied are you with how we handled {ticket}?", { required: true, help: "1 = very dissatisfied, 5 = very satisfied" });
    const q2 = Q("yesno", "Was your issue fully resolved?", { required: true });
    const q3 = Q("single", "What could we have done better?", {
      options: ["Response time", "Quality of the fix", "Communication / updates", "Had to explain more than once", "Other"],
      show_if: { q: q1.id, op: "lte", value: 3 }, required: true,
    });
    const q4 = Q("long", "Anything else you would like to tell us?", { help: "Optional — your support team reads every comment." });
    return [S("Your support experience", "", [q1, q2, q3, q4])];
  },
  nps: () => {
    const q1 = Q("nps", "How likely are you to recommend {product} to a colleague or another organisation?", { required: true, help: "0 = not at all likely, 10 = extremely likely" });
    const q2 = Q("long", "What is the main reason for your score?", { required: true });
    const q3 = Q("single", "What one thing would most improve your experience?", {
      options: ["Product reliability", "Speed / performance", "Support response", "Training", "Pricing", "Missing features"],
      show_if: { q: q1.id, op: "lte", value: 6 }, required: true,
    });
    const q4 = Q("matrix", "How would you rate us on…", { rows: ["Product quality", "Support", "Account management", "Value for money"], columns: ["Poor", "Fair", "Good", "Excellent"] });
    const q5 = Q("yesno", "Would you be willing to act as a reference for {company}?");
    const q6 = Q("short", "Who is the best person for us to speak to about this?", { show_if: { q: q5.id, op: "eq", value: "Yes" } });
    const s1 = S("Your overall view", "", [q1, q2, q3]);
    const s2 = S("A few details", "Two minutes more helps us act on your feedback.", [q4]);
    const s3 = S("Referral", "Thank you for the high score!", [q5, q6]);
    s1.jump = { q: q1.id, op: "gte", value: 9, to: s3.id };
    s2.jump = { q: null, op: "answered", value: null, to: "end" };
    return [s1, s2, s3];
  },
  onboarding: () => {
    const q1 = Q("rating", "Overall, how satisfied are you with the implementation of {product}?", { required: true });
    const q2 = Q("yesno", "Did go-live happen on the date agreed at kick-off?", { required: true });
    const q3 = Q("long", "What caused the delay?", { show_if: { q: q2.id, op: "eq", value: "No" } });
    const q4 = Q("matrix", "Please rate each part of the project", { rows: ["Project management", "Data migration", "Training", "Configuration"], columns: ["Poor", "Fair", "Good", "Excellent"], required: true });
    const q5 = Q("multi", "Which modules are your team already using every week?", { options: ["Payroll", "Leave", "Self-service", "Reports", "Loans", "Repayments"] });
    const q6 = Q("long", "What should we do differently for the next client?");
    return [S("Go-live", "", [q1, q2, q3]), S("The project", "", [q4, q5, q6])];
  },
  exit: () => {
    const q1 = Q("single", "What is the main reason you are leaving?", { required: true, options: ["Price", "Missing features", "Support / service quality", "Moving to a competitor", "Funding ended", "Company closed or merged", "Other"] });
    const q2 = Q("short", "Which system are you moving to?", { show_if: { q: q1.id, op: "eq", value: "Moving to a competitor" } });
    const q3 = Q("nps", "Despite leaving, how likely are you to recommend {product}?", { required: true });
    const q4 = Q("yesno", "Would you consider coming back in future?");
    const q5 = Q("long", "Anything we could have done to keep you?");
    return [S("Why you are leaving", "We are sorry to see you go. Your answers go to our management team.", [q1, q2, q3, q4, q5])];
  },
  demo: () => {
    const q1 = Q("rating", "How useful was the {product} demo?", { required: true });
    const q2 = Q("multi", "Which areas interested you most?", { options: ["Payroll & statutory", "Leave & attendance", "Self-service", "Loan origination", "M-Pesa repayments", "Reports & dashboards"] });
    const q3 = Q("single", "What would you like as the next step?", { required: true, options: ["A proposal / quotation", "A trial account", "A second demo for my team", "Not now"] });
    const q4 = Q("date", "When do you expect to make a decision?", { show_if: { q: q3.id, op: "neq", value: "Not now" } });
    const q5 = Q("long", "Anything we did not cover?");
    return [S("About the demo", "", [q1, q2, q3, q4, q5])];
  },
  custom: () => [S("Section 1", "", [Q("short", "Your question", { required: false })])],
  training: () => {
    const q1 = Q("rating", "How would you rate the training session?", { required: true });
    const q2 = Q("yesno", "Do you feel confident using {product} on your own now?", { required: true });
    const q3 = Q("multi", "Which topics need a refresher?", { options: ["Payroll approvals", "Leave setup", "Reports", "User management"], show_if: { q: q2.id, op: "eq", value: "No" } });
    const q4 = Q("long", "Comments on the trainer or materials");
    return [S("Training", "", [q1, q2, q3, q4])];
  },
};

export const surveyTemplates = [
  { key: "csat", type: "csat", name: "Ticket CSAT", icon: "iconoir-star", description: "1–5 rating after a ticket is resolved, with a follow-up question for low scores." },
  { key: "nps", type: "nps", name: "Quarterly NPS", icon: "iconoir-stats-up-square", description: "0–10 likelihood to recommend, reason, and a referral question for promoters." },
  { key: "onboarding", type: "onboarding", name: "Onboarding / go-live", icon: "iconoir-clipboard-check", description: "Sent after go-live: implementation, training and data migration." },
  { key: "exit", type: "exit", name: "Exit survey", icon: "iconoir-archive", description: "Why a client is leaving, with reason codes that match churn analysis." },
  { key: "demo", type: "demo", name: "Demo feedback", icon: "iconoir-eye", description: "Prospect view of a demo and the next step they want." },
  { key: "custom", type: "custom", name: "Blank", icon: "iconoir-page", description: "Start from an empty form." },
];

/* ---------- Defaults per type ---------- */

const defaultTriggers = (type) => ({
  ticket_resolved: { on: type === "csat", delay_hours: 1, products: [1, 2, 4, 5], categories: [], skip_reopened: true },
  golive: { on: type === "onboarding", days: 7 },
  schedule: { on: type === "nps", frequency: "Quarterly", next_run: daysFromNow(72), list: "dm_live", segments: [], min_tenure_days: 90 },
  exit: { on: type === "exit", days: 1 },
  demo: { on: type === "demo", hours: 2 },
});

const defaultAudience = (type) => ({
  throttle_days: type === "csat" ? 7 : 30,
  reminders: { on: type !== "csat", after_days: 3, max: 1 },
  expiry_days: 14,
  quiet_hours: true,
});

const EMAIL = {
  csat: ["How did we do on {ticket}?", "Hi {first_name},\n\nYour ticket {ticket} — \"{ticket_subject}\" — has been resolved. Could you rate how we handled it? It takes 30 seconds.\n\n{link}\n\nThe link works once and expires on {expiry}.\n\n{company} Support"],
  nps: ["A quick question about {product}", "Hi {first_name},\n\nHow likely are you to recommend {product} to a colleague? One question, then a short follow-up if you have time.\n\n{link}\n\nThank you for working with {company}."],
  onboarding: ["How did your {product} go-live go?", "Hi {first_name},\n\n{customer} has now been live on {product} for a week. Tell us how the implementation went:\n\n{link}\n\n{company} Implementation"],
  exit: ["Before you go — one last favour", "Hi {first_name},\n\nWe are sorry {customer} is leaving {product}. Five questions help us improve for everyone:\n\n{link}\n\n{company}"],
  demo: ["Thank you for your time — how was the demo?", "Hi {first_name},\n\nThank you for joining the {product} demo. What did you think, and what would you like next?\n\n{link}\n\n{company} Sales"],
  custom: ["We would value your feedback", "Hi {first_name},\n\nPlease take a few minutes to answer our survey:\n\n{link}\n\n{company}"],
};

const defaultDistribution = (type, channels) => {
  const [subject, body] = EMAIL[type] ?? EMAIL.custom;
  return {
    email: { on: channels.includes("email"), from: "Demulla Customer Success <cs@demulla.co.ke>", subject, body },
    sms: { on: channels.includes("sms"), text: `${company.name.split(" ")[0]}: ${type === "csat" ? "rate how we handled {ticket}" : "2-minute survey on {product}"} {link} (expires {expiry})` },
    whatsapp: { on: channels.includes("whatsapp"), text: "Hi {first_name}, " + (type === "csat" ? "how did we do on {ticket}? Tap to rate: {link}" : "we would love your feedback on {product}: {link}") },
    in_product: { on: channels.includes("in_product") },
    link: { on: channels.includes("link") },
  };
};

const defaultAutomations = (type) => {
  const scored = ["csat", "nps", "onboarding", "demo", "custom", "training"].includes(type) || type === "exit";
  const low = type === "nps" || type === "exit" ? 6 : 2;
  return [
    { key: "task", on: scored, threshold: low, assign: "csm", due_days: 2 },
    { key: "notify", on: scored && type !== "demo", threshold: type === "nps" ? 4 : 2, notify: "Head of Customer Success" },
    { key: "health", on: ["csat", "nps", "onboarding"].includes(type), threshold: low },
    { key: "complaint", on: type === "csat", threshold: 1 },
  ];
};

export const automationCatalogue = {
  task: { name: "Create a follow-up task", icon: "iconoir-task-list", text: "for the account's owner when the score is at or below the threshold." },
  notify: { name: "Notify a manager", icon: "iconoir-bell", text: "in-app and by email, with the response attached." },
  health: { name: "Lower the health score", icon: "iconoir-heart", text: "The Satisfaction indicator (15% of health) takes the latest score; a low one can move the band." },
  complaint: { name: "Open a complaint", icon: "iconoir-warning-triangle", text: "Logged under Support → Complaints with the comment as the description." },
};

/* ---------- Surveys ---------- */

const SV = (id, name, type, status, owner_id, channels, createdAgo, extra = {}) => {
  const template = extra.template ?? type;
  return {
    id,
    code: `SRV-${String(id).padStart(3, "0")}`,
    name,
    type,
    status,
    owner_id,
    channels,
    created: daysFromNow(-createdAgo),
    description: "",
    anonymity: "identified",
    language: "English",
    retention_months: 24,
    display: "section", // "section" | "question": one section or one question per screen
    brand: { colour: "#1b6ec2", logo: true },
    intro: "",
    thank_you: "Thank you — your answers have been recorded. Someone from our team reads every response.",
    sections: build[template](),
    triggers: defaultTriggers(type),
    audience: defaultAudience(type),
    distribution: defaultDistribution(type, channels),
    automations: defaultAutomations(type),
    funnel: { sent: 0, opened: 0, started: 0 },
    // Sample recipient context used for the preview pane.
    sample: { first_name: "Joyce", customer: "Gelisa", product: "Demulla LMS", ticket: "TKT-0406", ticket_subject: "STK push repayments not posting to loans", company: company.name },
    ...extra,
  };
};

export const surveys = reactive([
  SV(1, "Ticket CSAT", "csat", "active", 8, ["email", "sms", "in_product"], 400, {
    description: "Sent to the ticket's contact when a ticket is resolved. Clients on a Support API connection rate inside their HRM / LMS instead.",
    intro: "Your ticket has been resolved. Tell us how we did — it takes 30 seconds.",
    funnel: { sent: 41, opened: 35, started: 28 },
    display: "question",
  }),
  SV(2, "Quarterly NPS — Q4 2026", "nps", "active", 7, ["email", "whatsapp", "link"], 120, {
    description: "Sent every quarter to the decision makers of live customers who have been with us at least 90 days.",
    intro: "One question about your experience with us this quarter, then a short follow-up if you have time.",
    funnel: { sent: 38, opened: 31, started: 19 },
    brand: { colour: "#0f8a5f", logo: true },
    sample: { first_name: "Philip", customer: "Harambee Sacco", product: "Demulla LMS", ticket: "—", ticket_subject: "—", company: company.name },
  }),
  SV(3, "Onboarding / go-live", "onboarding", "active", 6, ["email"], 260, {
    description: "Sent 7 days after an onboarding project is handed over to Customer Success.",
    intro: "Your system has been live for a week. How did the implementation go?",
    funnel: { sent: 8, opened: 7, started: 6 },
    sample: { first_name: "Samuel", customer: "Mkopo Bora Credit", product: "Demulla LMS", ticket: "—", ticket_subject: "—", company: company.name },
  }),
  SV(4, "Exit survey", "exit", "active", 10, ["email"], 300, {
    description: "Sent the day after an exit is confirmed. Reasons map to the churn reason codes.",
    intro: "We are sorry to see you go. Five questions — your answers go to our management team.",
    funnel: { sent: 6, opened: 5, started: 4 },
    anonymity: "identified",
    sample: { first_name: "Esther", customer: "Tumaini NGO", product: "DeeHr360 HRM", ticket: "—", ticket_subject: "—", company: company.name },
  }),
  SV(5, "Demo feedback", "demo", "active", 2, ["email", "whatsapp"], 180, {
    description: "Sent two hours after a demo is marked completed — to prospects, not customers.",
    intro: "Thank you for your time today. What did you think?",
    funnel: { sent: 11, opened: 10, started: 7 },
    brand: { colour: "#6f42c1", logo: true },
    sample: { first_name: "Rose", customer: "Lakeview SACCO", product: "Demulla LMS", ticket: "—", ticket_subject: "—", company: company.name },
  }),
  SV(6, "Renewal NPS (60 days before renewal)", "nps", "paused", 7, ["email", "sms"], 90, {
    description: "Paused while the quarterly NPS covers the same contacts — the throttle would skip most of them anyway.",
    funnel: { sent: 9, opened: 6, started: 0 },
    triggers: { ...defaultTriggers("nps"), schedule: { on: true, frequency: "60 days before renewal", next_run: null, list: "dm_live", segments: ["MID", "ENT"], min_tenure_days: 300 } },
  }),
  SV(7, "Payroll approvals training feedback", "custom", "closed", 7, ["email", "link"], 75, {
    template: "training",
    description: "One-off survey after the payroll approvals refresher sessions.",
    funnel: { sent: 14, opened: 12, started: 9 },
    sample: { first_name: "Martin", customer: "Gatsby Insurance", product: "DeeHr360 HRM", ticket: "—", ticket_subject: "—", company: company.name },
  }),
  SV(8, "Annual relationship survey 2026", "custom", "draft", 7, ["email"], 4, {
    template: "nps",
    description: "Draft — a longer annual survey for enterprise accounts, being reviewed by the Head of Customer Success.",
  }),
]);
export const surveyById = (id) => surveys.find((s) => s.id === Number(id));

/* ---------- Responses ---------- */

const contactByName = (name, customerId) =>
  contacts.find((k) => k.name === name && (customerId == null || k.customer_id === customerId)) ?? contacts.find((k) => k.name === name) ?? null;
const primaryOf = (customerId) => contacts.find((k) => k.customer_id === customerId && k.role === "primary") ?? null;

let hash = 9;
const token = (prefix, seed) => {
  let h = 2166136261 ^ seed;
  let out = "";
  for (let i = 0; i < 10; i += 1) {
    h = Math.imul(h ^ (h >>> 13), 16777619) + i * 7919 + hash;
    out += "abcdefghjkmnpqrstuvwxyz23456789"[Math.abs(h) % 31];
  }
  hash += 1;
  return `${prefix}-${out}`;
};

const qs = (surveyId) => allQuestions(surveyById(surveyId));
const atTime = (day, n) => `${day}T${String(8 + (n % 9)).padStart(2, "0")}:${String((n * 13) % 60).padStart(2, "0")}:00`;

let rid = 0;
const R = (survey_id, { customer_id, contact, organisation, product_id, ticket = null, channel, date, score, answers, status = "completed" }) => {
  rid += 1;
  const k = contact ? contactByName(contact, customer_id) : customer_id ? primaryOf(customer_id) : null;
  return {
    id: rid,
    survey_id,
    token: token(surveyById(survey_id).type === "nps" ? "nps" : surveyById(survey_id).type, rid * 31),
    customer_id: customer_id ?? null,
    organisation: organisation ?? (customer_id ? customerById(customer_id)?.name : "—"),
    contact_id: k?.id ?? null,
    contact_name: contact ?? k?.name ?? "—",
    product_id,
    ticket,
    channel,
    submitted_at: atTime(date, rid),
    score,
    answers,
    status,
  };
};
const mainProduct = (customerId) => customerById(customerId)?.product_ids?.[0] ?? 1;

export const responses = reactive([]);

// 1. Ticket CSAT — the ratings already on tickets (API feedback first), then the email survey answers.
{
  const [q1, q2, q3, q4] = qs(1);
  const better = (score) => (score <= 3 ? { [q3.id]: score <= 1 ? "Quality of the fix" : score === 2 ? "Response time" : "Had to explain more than once" } : {});
  const seen = new Set();
  ticketFeedback.forEach((f) => {
    seen.add(f.number);
    const t = tickets.find((x) => x.id === f.ticket_id);
    responses.push(R(1, { customer_id: f.customer_id, contact: f.by, product_id: f.product_id, ticket: f.number, channel: "in_product", date: (f.at ?? hoursAgo(10)).slice(0, 10), score: f.rating, answers: { [q1.id]: f.rating, [q2.id]: f.reopen ? "No" : "Yes", ...better(f.rating), [q4.id]: f.comment } }));
    void t;
  });
  csatResponses.filter((c) => !seen.has(c.ticket)).forEach((c, i) => {
    const t = tickets.find((x) => x.number === c.ticket);
    responses.push(R(1, { customer_id: c.customer_id, contact: t?.contact, product_id: t?.product_id ?? mainProduct(c.customer_id), ticket: c.ticket, channel: i % 3 === 2 ? "sms" : "email", date: c.date, score: c.score, answers: { [q1.id]: c.score, [q2.id]: c.score >= 3 ? "Yes" : "No", ...better(c.score), [q4.id]: c.comment } }));
  });
  // Older tickets answered by email / SMS.
  [
    [2, 5, "Very helpful, thank you Faith.", 2, "TKT-0371", 2], [9, 5, "", 2, "TKT-0368", 6], [13, 4, "Good, but the first reply was generic.", 1, "TKT-0362", 9],
    [18, 5, "Sorted on the first call.", 1, "TKT-0359", 11], [1, 4, "", 1, "TKT-0355", 13], [5, 3, "Statutory change took three days to fix.", 1, "TKT-0351", 16],
    [10, 5, "Shop managers can now approve leave.", 1, "TKT-0347", 19], [19, 4, "Collections report fixed quickly.", 2, "TKT-0344", 22], [15, 5, "", 1, "TKT-0340", 27],
    [6, 2, "Waited two days for a first response.", 1, "TKT-0336", 31], [21, 5, "Export tracker fix deployed same day.", 4, "TKT-0331", 34], [2, 4, "", 2, "TKT-0327", 38],
    [16, 3, "Fixed, but we had to chase.", 1, "TKT-0322", 44], [9, 5, "Great turnaround.", 2, "TKT-0318", 50],
  ].forEach(([cid, score, comment, pid, ticket, ago], i) =>
    responses.push(R(1, { customer_id: cid, product_id: pid, ticket, channel: i % 4 === 3 ? "sms" : "email", date: daysFromNow(-ago), score, answers: { [q1.id]: score, [q2.id]: score >= 3 ? "Yes" : "No", ...better(score), [q4.id]: comment } })),
  );
}

// 2. Quarterly NPS — the same answers Customer Success → Feedback shows.
{
  const [q1, q2, q3, q4, q5, q6] = qs(2);
  const improve = { 6: "Support response", 3: "Product reliability", 4: "Product reliability", 12: "Product reliability", 13: "Pricing", 14: "Product reliability", 11: "Speed / performance", 9: "Support response", 10: "Product reliability" };
  const grid = (s) => {
    const g = s >= 9 ? ["Excellent", "Excellent", "Good", "Good"] : s >= 7 ? ["Good", "Fair", "Good", "Fair"] : ["Fair", "Poor", "Fair", "Poor"];
    return Object.fromEntries(q4.rows.map((r, i) => [r, g[i]]));
  };
  npsResponses.forEach((n, i) => {
    const answers = { [q1.id]: n.score, [q2.id]: n.comment || (n.score >= 9 ? "Reliable product and people who answer the phone." : "It does the job."), [q4.id]: grid(n.score) };
    if (n.score <= 6) answers[q3.id] = improve[n.id] ?? "Missing features";
    if (n.score >= 9) {
      answers[q5.id] = i % 2 === 0 ? "Yes" : "No";
      if (answers[q5.id] === "Yes") answers[q6.id] = n.contact;
      delete answers[q4.id]; // promoters jump straight to the referral section
    }
    responses.push(R(2, { customer_id: n.customer_id, contact: n.contact, product_id: mainProduct(n.customer_id), channel: i % 5 === 4 ? "whatsapp" : "email", date: n.date, score: n.score, answers }));
  });
}

// 3. Onboarding / go-live
{
  const [q1, q2, q3, q4, q5, q6] = qs(3);
  [
    [20, 4, "Yes", null, ["Good", "Fair", "Excellent", "Good"], ["Loans", "Repayments"], "Paybill approval should start at kick-off, not week 3.", 6],
    [11, 5, "Yes", null, ["Excellent", "Good", "Excellent", "Excellent"], ["Payroll", "Leave", "Self-service"], "", 9],
    [8, 4, "No", "Our own data was late — not your fault.", ["Good", "Good", "Good", "Fair"], ["Payroll", "Reports"], "More payroll parallel runs.", 14],
    [7, 3, "No", "Trainer changed twice and we lost a week.", ["Fair", "Good", "Fair", "Good"], ["Payroll"], "Keep one trainer for the whole project.", 21],
    [16, 2, "No", "Migration of leave balances was wrong.", ["Poor", "Poor", "Fair", "Fair"], ["Payroll"], "Test the migrated balances with us before go-live.", 160],
  ].forEach(([cid, score, onTime, delay, grid, mods, comment, ago], i) =>
    responses.push(R(3, {
      customer_id: cid, product_id: mainProduct(cid), channel: "email", date: daysFromNow(-ago), score,
      answers: { [q1.id]: score, [q2.id]: onTime, ...(delay ? { [q3.id]: delay } : {}), [q4.id]: Object.fromEntries(q4.rows.map((r, j) => [r, grid[j]])), [q5.id]: mods, [q6.id]: comment },
      status: i === 4 ? "completed" : "completed",
    })),
  );
}

// 4. Exit survey
{
  const [q1, q2, q3, q4, q5] = qs(4);
  [
    [12, "Funding ended", null, 6, "Maybe", "Nothing — the grant ended. The product was fine.", 17],
    [14, "Moving to a competitor", "SageHR", 3, "No", "Respond faster when payroll breaks at month end.", 62],
    [17, "Company closed or merged", null, 8, "Yes", "", 118],
    [3, "Support / service quality", null, 2, "No", "Fix the statutory tables before they bite the next client.", 1],
  ].forEach(([cid, reason, to, score, back, comment, ago]) =>
    responses.push(R(4, {
      customer_id: cid, product_id: 1, channel: "email", date: daysFromNow(-ago), score,
      answers: { [q1.id]: reason, ...(to ? { [q2.id]: to } : {}), [q3.id]: score, [q4.id]: back === "Maybe" ? "Yes" : back, [q5.id]: comment },
      status: cid === 3 ? "partial" : "completed",
    })),
  );
}

// 5. Demo feedback — prospects, so most have no customer record yet.
{
  const [q1, q2, q3, q4, q5] = qs(5);
  [
    [null, "Lakeview SACCO", "Rose Akinyi", 2, 5, ["Loan origination", "M-Pesa repayments"], "A proposal / quotation", 21, "Show us the board reports next time.", 3],
    [null, "Thika Steel Works", "George Kimani", 1, 3, ["Payroll & statutory"], "A second demo for my team", 45, "Too fast on statutory deductions.", 8],
    [21, null, "Mary Atieno", 1, 4, ["Payroll & statutory", "Leave & attendance"], "A trial account", 30, "", 12],
    [18, null, "Abdi Mohamed", 2, 5, ["Loan origination", "Reports & dashboards"], "A proposal / quotation", 60, "Our SACCO board wants to see it too.", 38],
    [13, null, "Henry Mugo", 2, 4, ["Loan origination"], "Not now", null, "Interesting for premium financing next year.", 26],
    [null, "Thika Steel Works", "George Kimani", 1, 2, ["Self-service"], "Not now", null, "Price is above our budget.", 52],
  ].forEach(([cid, org, contact, pid, score, areas, next, decideIn, comment, ago]) =>
    responses.push(R(5, {
      customer_id: cid, organisation: org ?? undefined, contact, product_id: pid, channel: ago % 2 ? "whatsapp" : "email", date: daysFromNow(-ago), score,
      answers: { [q1.id]: score, [q2.id]: areas, [q3.id]: next, ...(decideIn ? { [q4.id]: daysFromNow(decideIn) } : {}), [q5.id]: comment },
    })),
  );
}

// 7. Training feedback (closed)
{
  const [q1, q2, q3, q4] = qs(7);
  [
    [13, "Martin Gitau", 5, "Yes", null, "Clear and practical."], [13, "Jacqueline Mumbi", 4, "Yes", null, ""], [5, "Irene Nduta", 4, "No", ["Payroll approvals"], "Need one more session on approvals."],
    [5, "Eunice Wangui", 3, "No", ["Payroll approvals", "Reports"], "Too much in one hour."], [10, "Kevin Langat", 5, "Yes", null, ""], [15, "Purity Chege", 4, "Yes", null, "Good trainer."],
    [1, "Linda Mutiso", 5, "Yes", null, "Our payroll team loved it."], [16, "Victor Mutua", 2, "No", ["Payroll approvals", "User management"], "Session started late and ran out of time."],
  ].forEach(([cid, contact, score, confident, topics, comment], i) =>
    responses.push(R(7, {
      customer_id: cid, contact, product_id: 1, channel: i % 3 === 0 ? "link" : "email", date: daysFromNow(-(60 + i)), score,
      answers: { [q1.id]: score, [q2.id]: confident, ...(topics ? { [q3.id]: topics } : {}), [q4.id]: comment },
    })),
  );
}

export const responsesFor = (surveyId) => responses.filter((r) => r.survey_id === Number(surveyId)).sort((a, b) => (a.submitted_at < b.submitted_at ? 1 : -1));
export const responseById = (id) => responses.find((r) => r.id === Number(id));

/* ---------- Scores ---------- */

export const npsOf = (scores) => {
  const n = scores.length;
  if (!n) return { score: null, n: 0, promoters: 0, passives: 0, detractors: 0 };
  const promoters = scores.filter((s) => s >= 9).length;
  const detractors = scores.filter((s) => s <= 6).length;
  return { score: Math.round(((promoters - detractors) / n) * 100), n, promoters, passives: n - promoters - detractors, detractors };
};
export const csatOf = (scores) => (scores.length ? Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 10) / 10 : null);
export const scoreKind = (survey) => scoreQuestion(survey)?.type ?? null; // "rating" | "nps" | null

export const surveyStats = (survey) => {
  const rs = responsesFor(survey.id);
  const completed = rs.filter((r) => r.status === "completed").length;
  const scores = rs.map((r) => r.score).filter((s) => s !== null && s !== undefined);
  const kind = scoreKind(survey);
  const sent = Math.max(survey.funnel.sent, rs.length);
  const score = kind === "nps" ? npsOf(scores).score : kind === "rating" ? csatOf(scores) : null;
  return {
    sent,
    opened: Math.max(survey.funnel.opened, rs.length),
    started: Math.max(survey.funnel.started, rs.length),
    completed,
    responses: rs.length,
    rate: sent ? Math.round((completed / sent) * 100) : null,
    kind,
    score,
    scoreLabel: score === null ? "—" : kind === "nps" ? `NPS ${score > 0 ? "+" : ""}${score}` : `${score.toFixed(1)} / 5`,
    last: rs[0]?.submitted_at ?? null,
  };
};

/** Trend over time for the Results tab: by quarter for NPS, by month otherwise. */
export const surveyTrend = (survey) => {
  const kind = scoreKind(survey);
  const rs = responsesFor(survey.id);
  if (survey.id === 2) {
    const current = npsOf(rs.map((r) => r.score));
    return { labels: [...npsTrend.labels.slice(0, -1), "Q4 2026 (open)"], values: [...npsTrend.nps.slice(0, -1), current.score], counts: [24, 27, 22, current.n] };
  }
  const months = [];
  for (let i = 5; i >= 0; i -= 1) {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - i);
    months.push(d.toISOString().slice(0, 7));
  }
  const labels = months.map((m) => new Date(`${m}-01`).toLocaleString("en-GB", { month: "short", year: "2-digit" }));
  const values = months.map((m) => {
    const s = rs.filter((r) => r.submitted_at.startsWith(m)).map((r) => r.score);
    if (!s.length) return null;
    return kind === "nps" ? npsOf(s).score : csatOf(s);
  });
  // The CSAT survey has been running for over a year: earlier months come from the support history (npsTrend).
  if (survey.id === 1) [0, 1, 2].forEach((i) => (values[i] = values[i] ?? [4.2, 4.0, 3.9][i]));
  return { labels, values, counts: months.map((m) => rs.filter((r) => r.submitted_at.startsWith(m)).length) };
};

/* ---------- Sends: one single-use link per recipient ---------- */

export const sendStatuses = [
  { key: "queued", name: "Queued", tone: "secondary" },
  { key: "delivered", name: "Delivered", tone: "info" },
  { key: "opened", name: "Opened", tone: "primary" },
  { key: "started", name: "Started", tone: "warning" },
  { key: "completed", name: "Completed", tone: "success" },
  { key: "expired", name: "Expired", tone: "dark" },
  { key: "bounced", name: "Bounced", tone: "danger" },
];
export const sendStatus = (key) => sendStatuses.find((s) => s.key === key) ?? sendStatuses[0];

let sendId = 0;
export const sends = [];
responses.forEach((r) => {
  sendId += 1;
  const sentAt = new Date(new Date(r.submitted_at).getTime() - (2 + (r.id % 30)) * 3600000).toISOString();
  sends.push({ id: sendId, survey_id: r.survey_id, token: r.token, contact_name: r.contact_name, customer_id: r.customer_id, organisation: r.organisation, channel: r.channel, sent_at: sentAt, status: r.status === "partial" ? "started" : "completed", reminders: r.id % 4 === 0 ? 1 : 0, response_id: r.id, ticket: r.ticket });
});
// Recipients who have not answered (yet).
[
  [1, 6, "Agnes Chebet", "email", 0.4, "delivered", "TKT-0425"], [1, 19, "Faith Njoki", "in_product", 3, "opened", "TKT-0414"], [1, 3, "Amina Juma", "email", 30, "expired", "TKT-0401"],
  [1, 16, "Victor Mutua", "sms", 70, "bounced", "TKT-0412"], [2, 2, "Agnes Nyambura", "email", 20, "opened", null], [2, 9, "Esther Chepkemoi", "email", 20, "delivered", null],
  [2, 8, "Patrick Cheruiyot", "whatsapp", 19, "started", null], [2, 11, "Grace Nakato", "email", 19, "queued", null], [2, 18, "Abdi Mohamed", "email", 21, "opened", null],
  [3, 8, "Daniel Rotich", "email", 2, "delivered", null], [5, null, "Rose Akinyi", "whatsapp", 1, "opened", null], [6, 4, "Dr. Samuel Owino", "email", 120, "expired", null],
].forEach(([survey_id, cid, name, channel, daysAgo, status, ticket]) => {
  sendId += 1;
  sends.push({
    id: sendId, survey_id, token: token(survey_id === 2 || survey_id === 6 ? "nps" : surveyById(survey_id).type, sendId * 17), contact_name: name, customer_id: cid,
    organisation: cid ? customerById(cid).name : "Lakeview SACCO", channel, sent_at: hoursAgo(daysAgo * 24), status, reminders: status === "opened" ? 1 : 0, response_id: null, ticket,
  });
});
export const sendsFor = (surveyId) => sends.filter((s) => s.survey_id === Number(surveyId)).sort((a, b) => (a.sent_at < b.sent_at ? 1 : -1));
export const sendByToken = (t) => sends.find((s) => s.token === t);

/* ---------- Automation runs ---------- */

const actionText = (key, r, survey) => {
  const c = r.customer_id ? customerById(r.customer_id) : null;
  const owner = c ? staffName(c.csm_id) : staffName(survey.owner_id);
  return {
    task: `Follow-up task "Call ${r.contact_name} about their ${r.score} score" → ${owner}`,
    notify: "Grace Njeri (Head of Customer Success) notified",
    health: c ? `${c.name}: Satisfaction indicator set to ${survey.type === "nps" ? r.score * 10 : r.score * 20}` : "No customer record — skipped",
    complaint: `Complaint opened for ${c?.name ?? r.organisation} (Service quality)`,
  }[key];
};

export const automationRunsFor = (survey) => {
  const runs = [];
  let n = 0;
  responsesFor(survey.id).forEach((r) => {
    if (r.score === null || r.score === undefined) return;
    survey.automations.forEach((a) => {
      if (!a.on || r.score > a.threshold) return;
      if (a.key === "health" && !r.customer_id) return;
      n += 1;
      runs.push({ id: `${survey.id}-${n}`, at: r.submitted_at, response_id: r.id, rule: a.key, score: r.score, organisation: r.organisation, contact: r.contact_name, action: actionText(a.key, r, survey), result: n % 7 === 3 ? "Done" : n % 5 === 2 ? "Open" : "Done" });
    });
  });
  return runs;
};

/* ---------- Respondent page helpers ---------- */

/** What a token opens: the survey and the recipient's context, or why it cannot. */
export const resolveToken = (raw) => {
  const t = String(raw ?? "").trim().toLowerCase();
  if (!t || t === "invalid" || t.length < 3) return { state: "invalid" };
  if (t === "expired") return { state: "expired", survey: surveyById(1) };
  if (t === "used") return { state: "used", survey: surveyById(1) };
  const send = sendByToken(t);
  if (send) {
    const survey = surveyById(send.survey_id);
    if (send.status === "completed") return { state: "used", survey };
    if (send.status === "expired") return { state: "expired", survey };
    const c = send.customer_id ? customerById(send.customer_id) : null;
    return { state: "open", survey, context: { ...survey.sample, first_name: send.contact_name.split(" ").slice(-2)[0], customer: send.organisation, ticket: send.ticket ?? "—", product: productById(c?.product_ids?.[0])?.short ?? survey.sample.product } };
  }
  const pub = t.match(/^pub-srv-(\d+)$/);
  if (pub) {
    const survey = surveyById(Number(pub[1]));
    return survey ? { state: survey.status === "closed" ? "expired" : "open", survey, context: { ...survey.sample, first_name: "", customer: "" }, anonymous: true } : { state: "invalid" };
  }
  const prefixes = [["nps", 2], ["onb", 3], ["exit", 4], ["demo", 5], ["trn", 7]];
  const match = prefixes.find(([p]) => t.startsWith(p));
  const survey = surveyById(match ? match[1] : 1);
  if (survey.id === 1) {
    const ticket = tickets.find((x) => x.number === "TKT-0406");
    return { state: "open", survey, context: { ...survey.sample, first_name: "Brian", customer: customerById(ticket.customer_id).name, ticket: ticket.number, ticket_subject: ticket.subject, product: "Demulla LMS" } };
  }
  return { state: "open", survey, context: survey.sample };
};

/* ---------- Connections that can show a survey in-product ---------- */

export const inProductConnections = () => connectedApps.filter((a) => a.status === "active");

/* ---------- Change history (Settings & history tab) ---------- */

const CL = (n, surveyId, setting, oldValue, newValue, by, reason, ago) => ({
  id: 5000 + n,
  area: `survey:${surveyId}`,
  setting,
  old: oldValue,
  new: newValue,
  by,
  reason,
  at: `${daysFromNow(-ago)}T${String(9 + (n % 7)).padStart(2, "0")}:${String((n * 11) % 60).padStart(2, "0")}`,
  effective: daysFromNow(-ago),
});
configLog.push(
  CL(1, 1, "Status", "Draft", "Active", "Faith Achieng", "Replaces the old Google Form CSAT", 400),
  CL(2, 1, "Trigger · delay after resolution", "24 hours", "1 hour", "Faith Achieng", "Response rate was dropping the next day", 140),
  CL(3, 1, "Channel · In-product (Support API)", "Off", "On", "Faith Achieng", "HRM and LMS now show the rating prompt after ticket.resolved", 90),
  CL(4, 1, "Automation · open complaint", "Off", "On (score 1)", "Grace Njeri", "Coastline's 1-star rating sat unanswered for a week", 20),
  CL(5, 2, "Status", "Draft", "Active", "Grace Njeri", "Q4 round approved", 25),
  CL(6, 2, "Audience", "Primary contacts", "Decision makers · live customers", "Grace Njeri", "NPS should reflect the people who renew", 60),
  CL(7, 2, "Throttle", "14 days", "30 days", "Grace Njeri", "Contacts complained about survey fatigue", 33),
  CL(8, 3, "Trigger · days after go-live", "14", "7", "Kevin Mwangi", "Feedback is sharper while the project is fresh", 70),
  CL(9, 4, "Owner", "Grace Njeri", "Samuel Kariuki", "Grace Njeri", "Samuel runs exit interviews", 45),
  CL(10, 5, "Channel · WhatsApp", "Off", "On", "Brian Otieno", "Prospects reply faster on WhatsApp", 50),
  CL(11, 6, "Status", "Active", "Paused", "Grace Njeri", "Overlaps the quarterly NPS round", 30),
  CL(12, 7, "Status", "Active", "Closed", "Grace Njeri", "Training round finished", 55),
  CL(13, 8, "Status", "—", "Draft", "Grace Njeri", "New annual survey for enterprise accounts", 4),
);

/* ---------- Text answers: simple keyword tags ---------- */

const THEMES = [
  ["Speed", /\b(slow|fast|quick|quickly|took|wait|waited|late|turnaround|same day|hour)\b/i],
  ["Support", /\b(support|response|reply|chase|explain|helpful|faith|answer)\b/i],
  ["Reliability", /\b(fail|failures|failed|again|outage|broke|break|reliable|wrong|incident)\b/i],
  ["Payroll", /\b(payroll|paye|statutory|payslip)\b/i],
  ["M-Pesa / LMS", /\b(m-pesa|stk|loan|lms|repayment|borrower|paybill|collections)\b/i],
  ["Price", /\b(price|expensive|budget|cost|billing)\b/i],
  ["Training", /\b(training|trainer|session|refresher)\b/i],
  ["Praise", /\b(thank|great|loved|loves|good|clear|solid|works|happy|helpful)\b/i],
];
export const keywordTags = (text) => THEMES.filter(([, re]) => re.test(text ?? "")).map(([t]) => t);

/** A fresh survey from a template (New survey modal). */
export const createSurvey = ({ name, template, owner_id }) => {
  const t = surveyTemplates.find((x) => x.key === template) ?? surveyTemplates[5];
  const id = Math.max(...surveys.map((s) => s.id)) + 1;
  const channels = t.type === "csat" ? ["email", "in_product"] : ["email"];
  const s = SV(id, name || t.name, t.type, "draft", owner_id, channels, 0, { template: t.key, intro: "", funnel: { sent: 0, opened: 0, started: 0 } });
  surveys.push(s);
  configLog.unshift({ id: 6000 + id, area: `survey:${id}`, setting: "Status", old: "—", new: `Draft (from template "${t.name}")`, by: "Jane Wanjiku", reason: "Survey created", at: new Date().toISOString(), effective: daysFromNow(0) });
  return s;
};

export const customersForFilter = customers.filter((c) => c.lifecycle !== "former");
